import {useEffect, useState} from 'react'

import Icon from '../Icon'
import Progress from '../Progress'
import {showErrorPopup} from '~/services/popup'
import {pauseAudio, playAudio, resumeAudio, subscribeOnEvents, synthesizeDemo} from '~/services/synthesis'
import {useObjectUrl} from '~/utils/useObjectUrl'

import type {SoundStatus} from '~/services/synthesis'

const exampleTexts: {[K in string]?: string} & {en: string} = {
    ru: 'Приветствуем на демонстрации технологии синтеза речи.',
    en: 'Welcome to the demonstration of speech synthesis technology.',
    kk: 'Сөйлеу синтезі технологиясының көрсетіліміне қош келдіңіз.',
}

type Props = {
    voiceId: string
    language: string
    activeVoice: string
    setActiveVoice: (voiceId: string) => void
}

export default function SynthesisVoicesPlayer({voiceId, language, activeVoice, setActiveVoice}: Props) {
    const [ready, setReady] = useState(true)
    const [soundStatus, setSoundStatus] = useState<SoundStatus>('stopped')
    const [objectUrl, setBlob] = useObjectUrl()

    useEffect(() => subscribeOnEvents({
        ended: () => setSoundStatus('stopped'),
        play: () => activeVoice == voiceId
            ? setSoundStatus('playing')
            : setSoundStatus('stopped'),
        pause: () => activeVoice == voiceId
            ? setSoundStatus(objectUrl ? 'paused' : 'stopped')
            : setSoundStatus('stopped'),
    }), [objectUrl, activeVoice, voiceId])

    useEffect(() => {
        if (objectUrl)
            playAudio(objectUrl)
        else
            setSoundStatus('stopped')

        return pauseAudio
    }, [objectUrl])

    const handleClick = () => {
        setActiveVoice(voiceId)

        switch (soundStatus) {
        case 'stopped':
            return objectUrl ? playAudio(objectUrl) : play()
        case 'playing':
            return pauseAudio()
        case 'paused':
            return resumeAudio()
        }
    }

    const play = () => {
        pauseAudio()
        setReady(false)

        const phrase = exampleTexts[language] || exampleTexts.en

        synthesizeDemo(voiceId, phrase, 1, language, 'neutral')
            .then(setBlob)
            .catch(showErrorPopup)
            .finally(() => setReady(true))
    }

    if (!ready)
        return <Progress className='progress-icon_medium'/>

    return <button
        onClick={handleClick}
        disabled={!voiceId}
    >
        <Icon
            className='synthesis__play-icon synthesis__play-icon_small'
            name={soundStatus == 'playing' ? 'pause' : 'play'}
        />
    </button>
}
